import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { organizationType } from '@openx/types/organization';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getOrganizationTypesLabels, organizationFields, organizationTypesOptions } from '../../../../constants';
import type { OrganizationFormData } from '../../../../types';
import { useOrganizationContext } from '../../../../utils/context/OrganizationContext';

type OrganizationTypeFieldProps = {
  isEdit?: boolean;
  initialFormValues: OrganizationFormData;
};

export const OrganizationTypeField = ({ isEdit, initialFormValues }: OrganizationTypeFieldProps) => {
  const { useOrganizationPermissions } = useOrganizationContext();
  const { isInternalUser } = useOrganizationPermissions();
  const { t } = useTranslation();

  const observableParentAccount = useWatch<OrganizationFormData, typeof organizationFields.PARENT_ACCOUNT>({
    name: organizationFields.PARENT_ACCOUNT,
  });

  const observableDspRestriction = useWatch<OrganizationFormData, typeof organizationFields.DSP_RESTRICTION>({
    name: organizationFields.DSP_RESTRICTION,
  });

  const observableShouldAllowToUpdateOrganizationType = useWatch<
    OrganizationFormData,
    typeof organizationFields.SHOULD_ALLOW_TO_UPDATE_ORGANIZATION_TYPE
  >({
    name: organizationFields.SHOULD_ALLOW_TO_UPDATE_ORGANIZATION_TYPE,
  });

  const organizationTypeLabels = getOrganizationTypesLabels(t);

  const isOrganizationTypeLockedForBuyers = useMemo(
    () =>
      initialFormValues?.organization_type === organizationType.Buyer &&
      observableDspRestriction &&
      observableDspRestriction.length > 0 &&
      initialFormValues?.dsp_restriction !== undefined,
    [initialFormValues?.organization_type, observableDspRestriction, initialFormValues?.dsp_restriction]
  );

  const isTopLevelOrganization = observableParentAccount ? observableParentAccount?.depth === 0 : true;

  const shouldDisableOrganizationType = useMemo(
    () =>
      !isInternalUser ||
      isOrganizationTypeLockedForBuyers ||
      (!isEdit && !isTopLevelOrganization) ||
      (isEdit && !observableShouldAllowToUpdateOrganizationType),
    [
      isInternalUser,
      isOrganizationTypeLockedForBuyers,
      isEdit,
      isTopLevelOrganization,
      observableShouldAllowToUpdateOrganizationType,
    ]
  );
  return (
    <>
      <RHFAutoCompleteVirtualize
        name={organizationFields.ORGANIZATION_TYPE}
        getOptionLabel={option => organizationTypeLabels[option]}
        disableClearable
        options={organizationTypesOptions}
        sx={{ mt: 2 }}
        textFieldProps={{
          label: t('Organization Type'),
          placeholder: t('[ Select Organization Type ]'),
        }}
        data-test="organization-type"
        disabled={shouldDisableOrganizationType}
      />

      {shouldDisableOrganizationType && !isOrganizationTypeLockedForBuyers && (
        <Alert data-test="organization-type-inherited-warning" severity="warning" sx={{ mt: 2, alignItems: 'center' }}>
          <Typography>
            {t(
              'Organization type is inherited from the parent organization and cannot be changed. If you have any questions, please contact support'
            )}
          </Typography>
        </Alert>
      )}

      {isOrganizationTypeLockedForBuyers && (
        <Alert severity="warning" sx={{ mt: 2, alignItems: 'center' }}>
          <Typography>
            {t('Buyer Organization types cannot be changed. If necessary, create a new organization.')}
          </Typography>
        </Alert>
      )}
    </>
  );
};
